import { startTransition, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { api, ApiError, type Developer } from '../api';
import { LanguageSwitch, Logo } from '../components/ui';
import { PRODUCT_NAME } from '../productName';
import { errorText, useT } from '../i18n';
import { isLaunchHidden } from '../launchHidden';
import { useSession } from '../session';
import { afterLoginPath, authFormProps, emailFieldProps, MIN_PASSWORD, passwordFieldProps, passwordLengthOk } from '../authForms';
import { cameFromAiConnect, loginCredentials, loginEmailFirst } from '../loginFromAi';
import { LastUsedBadge, OAuthButtons } from '../components/OAuthButtons';
import { PasswordField } from '../components/PasswordField';
import { deviceStorage, lastMethod, readLastLogin, recordLogin, rememberDefault, setRemember, type LastLogin } from '../lastLogin';
import { PROVIDER_NAMES } from '../devOAuth';
import { clearResetEmail, peekResetEmail, tabStorage } from '../resetEmail';
import type { OAuthLoginNotice } from './OAuthCallback';
import { adSourceFromSearch, inviteErrorKey, inviteNotAppliedReason, isInviteError, type InviteError, inviteFieldState, inviteFromSearch, inviteInputToSend, readInviteInput, searchWithoutInvite, signupInviteError, type InviteCreditOffer, type InviteSignupNotice, type SignupInviteRequirement } from '../inviteCode';
import { INVITE_CODE_PATH, InviteNotAppliedNotice } from '../components/InviteNotAppliedNotice';
import { loadInviteCreditOffer, loadSignupInviteRequirement } from '../devOAuthClient';
import { money } from '../money';
import { markAdVisit } from '../adVisit';
import { searchWithoutVia, viaFromSearch } from '../signupVia';

/**
 * 登録の画面の招待コードの欄に使う GET /v1/platform/info の inviteCredit（2026-09-30 console/invite-fields・inviteCode.ts readInviteCreditOffer）。
 * 読み込み中・失敗は unknown = 今までの欄（額なしの説明）。WHY 読み込み中に欄を隠さないか: 招待の無い本体（off）はまれで、
 *   ふつうの本体で「欄が後から出てきてフォームがずれる」方が多い。応答はログイン画面と共有して使い回す（devOAuthClient.ts）ので、
 *   ログイン画面から来た人にはたいてい最初から分かっている
 */
function useInviteCreditOffer(): InviteCreditOffer {
  const [o, setO] = useState<InviteCreditOffer>({ kind: 'unknown' });
  useEffect(() => {
    let alive = true;
    void loadInviteCreditOffer().then((v) => alive && setO(v));
    return () => {
      alive = false;
    };
  }, []);
  return o;
}

/**
 * 招待制か（GET /v1/platform/info の signupRequiresInvite・inviteCode.ts の readSignupRequiresInvite・2026-10-06 core/invite-only）。
 * 読み込み中・失敗は unknown = 欄は出し「任意」とは言わない・押す前には止めない（正はサーバーの 400 invite_code_required）。応答は上と共有（1 回の fetch）
 */
function useSignupInviteRequirement(): SignupInviteRequirement {
  const [r, setR] = useState<SignupInviteRequirement>('unknown');
  useEffect(() => {
    let alive = true;
    void loadSignupInviteRequirement().then((v) => alive && setR(v));
    return () => {
      alive = false;
    };
  }, []);
  return r;
}

// 2026-09-27 creator/auth-forms: 登録とログインを別のコンポーネント・別のフォームに分けた（以前は AuthPage 1 つを mode で切り替え）。
// WHY: /login ↔ /signup の移動で React が同じフォームの DOM を使い回し（同じ位置・同じ型）、入力も残っていた。パスワード管理は
//   1 つのフォームの中身が入れ替わると「新しいアカウント」と認識しにくく、登録で生成を出さない・ログインの値を登録として保存する。
//   属性（autocomplete・name・passwordrules）は authForms.ts の 1 か所で作る。

/**
 * ログイン画面と登録画面の共通の枠（左の場所の名前と説明・右のフォームの置き場）。フォームそのものは各画面が持つ。
 *
 * 2026-09-28 creator/plain-words（founder「アプリの バックエンドを、ひとつの空に。 意味がわからんので。抽象的な言い回しはやめていいよ」・
 *   MASTER-PLAN §1c「ダッシュボードに宣伝の言葉・キャッチコピーを置かない」）: 以前は左に大きな決め台詞（auth.headline1〜3）・
 *   SDK の名前空間を並べた飾り（auth-orbit）・「<script> → Kumo.init() → ship.」の 1 行があった。どれも「ここで何をするか」を伝えないので消し、
 *   場所の名前（auth.sideTitle）と、ログインすると何ができるかの具体的な 1 文（auth.sideBody）だけを置く。
 *   左の面そのもの（暗い地・ロゴ）は残す: 右だけにすると広い画面で左右の余白ばかりになり、どこのログインかも一目でわからなくなるため。
 *   小さい画面では左を出さない（styles.css .auth-art の max-width: 860px）= 右のフォームの上のロゴだけ
 */
export function AuthLayout({ children }: { children: ReactNode }) {
  const { t } = useT();
  return (
    <main className="auth">
      {/* 以前は aria-hidden（飾りだったため）。いまは場所の名前と説明の文なので、読み上げにも出す */}
      <section className="auth-art">
        <div className="auth-brand">
          <Logo size={36} />
          <span>{PRODUCT_NAME}</span>
        </div>
        <div className="auth-side">
          <h1 className="auth-side-title">{t('auth.sideTitle')}</h1>
          <p className="auth-side-body">{t(isLaunchHidden('payouts') ? 'auth.sideBodyLaunch' : 'auth.sideBody')}</p>
        </div>
      </section>
      <section className="auth-panel">{children}</section>
    </main>
  );
}

/** フォームの上の段（小さい画面のロゴ・言語の切り替え）と見出し */
export function FormTop({ title, lead }: { title: string; lead: string }) {
  return (
    <>
      <div className="auth-form-top">
        <div className="auth-mobile-brand">
          <Logo size={28} />
          <span>{PRODUCT_NAME}</span>
        </div>
        <LanguageSwitch className="auth-lang" />
      </div>
      <h2>{title}</h2>
      <p className="muted">{lead}</p>
    </>
  );
}

export function ErrorLine({ error }: { error: string | null }) {
  if (!error) return null;
  return (
    <p className="alert alert-error" role="alert">
      {error}
    </p>
  );
}

/**
 * 「この端末にメールとログイン方法を保存する」のチェックと、覚えている内容（ログイン・登録で共通）。
 * 外したら保存済みをすぐ消す（setRemember）。onForget = 画面側の後始末（欄に入れたメールを消す等）
 */
function useRemember() {
  const store = deviceStorage();
  const [saved, setSaved] = useState<LastLogin | null>(() => readLastLogin(store));
  const [remember, setRememberState] = useState(() => rememberDefault(saved));
  const change = (v: boolean) => {
    setRememberState(v);
    setRemember(store, v);
    setSaved(readLastLogin(store));
  };
  // 成功したときに呼ぶ（チェックが外れている端末では lastLogin.ts が何も書かない）
  const record = (email: string) => recordLogin(store, 'email', email);
  return { saved, remember, change, record };
}

function RememberCheck({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  const { t } = useT();
  return (
    // name を付けない: パスワード管理・送信の中身に混ぜない（この値はこの端末の中だけで使う）
    <label className="check auth-remember" htmlFor="remember-device">
      <input id="remember-device" type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>{t('auth.rememberDevice')}</span>
    </label>
  );
}

export function LoginPage() {
  const { signIn } = useSession();
  const { t, tr } = useT();
  const navigate = useNavigate();
  const location = useLocation();
  // oauthError: Google 等から戻って入れなかったとき（pages/OAuthCallback.tsx が渡す。email_exists・verify_email_first 等の案内）
  const state = location.state as ({ from?: unknown; notice?: 'resetDone' } & Partial<OAuthLoginNotice>) | null;
  const rem = useRemember();
  const last = lastMethod(rem.saved);
  // 前回メールで入った端末では、覚えているメールを欄に入れておく（パスワードはブラウザ / パスワード管理に任せる）
  const savedEmail = rem.saved?.remember ? (rem.saved.email ?? '') : '';
  // パスワードの再設定の直後（2026-10-09 core/reset-username）: 再設定したアカウントのメールを先に入れる（覚えているメールより優先）。
  //   同じタブの sessionStorage で受け取り、描いた後に消す（URL には載せない・WHY は ../resetEmail.ts）。
  //   パスワード管理はこの username の欄の値で、再設定で保存した新しいパスワードを候補に出す
  const [resetEmail] = useState(() => peekResetEmail(tabStorage()));
  useEffect(() => clearResetEmail(tabStorage()), []);
  // 送信のときは loginCredentials が欄の値も見る（下の submit）。名前の State = 送信で使う email / password と分けるため
  const [emailState, setEmail] = useState(resetEmail ?? savedEmail);
  const [passwordState, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [submits, setSubmits] = useState(0);

  const onRemember = (v: boolean) => {
    rem.change(v);
    // 外したら、覚えていた値を欄からも消す（共有の端末で「外したのに残っている」に見せない）。自分で打ち直した値は消さない
    if (!v && savedEmail && emailState === savedEmail) setEmail('');
  };

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setSubmits((n) => n + 1); // パスワードを表示中なら隠す（PasswordField の hideSignal）
    // 状態が空でも欄に値があればそれを送る（2026-10-31 core/login-from-ai・自動の入力の道具で「押しても何も起きない」を防ぐ。WHY は ../loginFromAi.ts）
    const { email, password } = loginCredentials({ email: emailState, password: passwordState }, new FormData(e.currentTarget));
    // ログインでは長さを先回りで止めない（規則ができる前の短いパスワードの人がいても、判断はサーバーに任せる）。空だけ止める
    if (!email.trim() || !password) {
      setError(t('error.invalidCredentials'));
      return;
    }
    setBusy(true);
    try {
      const r = await api<{ token: string; developer: Developer }>('/v1/developers/login', { method: 'POST', body: { email, password, label: 'dashboard' }, auth: false });
      rem.record(email);
      // 成功したら History API で遷移する（パスワード管理は「送信 → 遷移」で成功と判断し、保存を出す）。
      // 行き先はログイン前に開こうとした画面（App.tsx の catch-all が state.from に入れる）。外部へは行かない（afterLoginPath）
      // navigate と signIn を同じ transition にまとめる: react-router の BrowserRouter は遷移を startTransition で反映するので、
      // signIn だけ先に反映されると、ログイン済みの /login の Navigate（App.tsx）がもう 1 回 replace で飛ばし、履歴が二重になる
      startTransition(() => {
        navigate(afterLoginPath(state?.from));
        signIn(r.token, r.developer);
      });
    } catch (err) {
      // ログイン画面の 401 は「セッション切れ」ではなく「メールかパスワードが違う」
      setError(errorText(err as ApiError, { unauthorized: 'error.invalidCredentials' }));
      setBusy(false);
    }
  };

  // AI のアプリの Connect から来た（state.from = /mcp/authorize?request=…）: メール＋パスワードを上に・見出しの下の 1 行を替える
  //   （2026-10-31 core/login-from-ai・founder「この案で進めて」・WHY は ../loginFromAi.ts）。ふつうのログインは前回の方法だけで決まる（今までどおり）
  const fromAi = cameFromAiConnect(state?.from);
  const emailFirst = loginEmailFirst(last, fromAi);
  // Google / Apple / GitHub（サーバーの oauthProviders にあるものだけ）+ パスキーの置き場。ログインの後はパスワードと同じ行き先へ
  // passkey: ログイン画面だけパスキーのボタンを置く（登録画面には置かない = OAuthButtons の説明）
  const external = <OAuthButtons returnTo={afterLoginPath(state?.from)} last={last} divider={emailFirst ? 'above' : 'below'} passkey />;

  return (
    <AuthLayout>
      <form className="auth-form" {...authFormProps('login', '/login')} onSubmit={(e) => void submit(e)}>
        <FormTop title={t('auth.loginTitle')} lead={t(fromAi ? 'auth.loginLeadAiConnect' : 'auth.loginLead')} />
        {state?.notice === 'resetDone' && (
          <p className="alert alert-ok" role="status">
            {t('recover.resetDone')}
          </p>
        )}
        {state?.oauthError && state.provider && (
          // 同じメールのアカウントがある（email_exists）等。パスワードでログインしてから /account で追加する道を示す（自動では紐づけない）
          <p className="alert alert-warn" role="alert">
            {t(`oauth.error.${state.oauthError}`, { provider: PROVIDER_NAMES[state.provider] })}
          </p>
        )}
        {!emailFirst && external}
        <div className="auth-fields">
          <label className="field" htmlFor="email">
            <span>{t('auth.email')}</span>
            <input {...emailFieldProps()} value={emailState} onChange={(e) => setEmail(e.target.value)} required />
          </label>
          <PasswordField
            label={t('auth.password')}
            aside={
              <Link className="auth-forgot" to="/forgot-password">
                {t('auth.forgotLink')}
              </Link>
            }
            hideSignal={submits}
            inputProps={{ ...passwordFieldProps('current'), value: passwordState, onChange: (e) => setPassword(e.target.value), required: true }}
          />
          <RememberCheck checked={rem.remember} onChange={onRemember} />
          <ErrorLine error={error} />
          <button type="submit" className="btn btn-primary btn-block auth-submit" disabled={busy}>
            {busy ? t('auth.busy') : t('auth.logIn')}
            {/* 「前回使った」の印は本当に前回メールで入った端末だけ（AI から来て上に置いただけの時は付けない = 嘘の印を出さない） */}
            {last === 'email' && !busy && <LastUsedBadge />}
          </button>
        </div>
        {emailFirst && external}
        {/* 戻り先（state.from）を新規登録へ引き継ぐ（2026-10-04 founder: Claude から連携するとき「ログインしか選べない・新規登録も必要」。
            登録の後に /mcp/authorize などの元の画面へ戻すため） */}
        <p className="auth-switch">{tr('auth.newHere', { link: <Link to="/signup" state={typeof state?.from === 'string' ? { from: state.from } : undefined}>{t('auth.createAnAccount')}</Link> })}</p>
      </form>
    </AuthLayout>
  );
}

export function SignupPage() {
  const { signIn } = useSession();
  const { t, tr } = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const rem = useRemember();
  // Google / GitHub の登録が招待コードで断られて戻ってきた（pages/OAuthCallback.tsx が渡す・inviteCode.ts の InviteSignupNotice）
  const back = location.state as Partial<InviteSignupNotice> | null;
  // 招待コード（2026-09-29 creator/launch-money）。メールの登録は signup の本文に、Google / GitHub は控え → exchange に付ける（inviteCode.ts）。
  // 戻ってきたときは欄を空から（控えは一回限りで消えている。断られたコードを入れ直させない = 直したコードか空で）
  // 2026-10-06 core/invite-only（founder「新規登録を招待制に」）: 招待制の本体（signupRequiresInvite: true）では必須。
  //   WHY（当時）: X のアカウントを動かすと LP から知らない人が登録してしまう。サーバーもコード無しを 400 invite_code_required で断る（アカウントを作らない）
  // 2026-10-29 core/open-signup-build（founder「招待コードなくても使えるようにしよう。招待コードはクレジット獲得のみ」）: 必須かどうかはサーバーの旗
  //   （KUMO_SIGNUP_REQUIRES_INVITE → platform/info の signupRequiresInvite）で切り替わる。10/31 から本番は false = 欄は「(optional)」・クレジットのためだけ。
  //   false の間は使えないコードでもアカウントはでき、応答の inviteNotApplied で「使えませんでしたが、できました」の 1 枚を出す（下の notApplied）
  // 2026-10-16 console/invite-link: /signup?invite=<CODE> で来たら欄にそのコードを入れておく（inviteCode.ts の inviteFromSearch・形が変なら空のまま）。
  //   読み込み中・招待制の決まり（inviteFieldState / inviteInputToSend / signupInviteError）は手で入れたときと同じ = 欄の値を変えるだけ
  const [invite, setInvite] = useState(() => inviteFromSearch(location.search) ?? '');
  // /early の「Continue with GitHub / Google」から来た（?via=github・2026-10-22 site/early-inline-signup・WHY と守りは ../signupVia.ts）。
  //   handoff の間は「GitHub を開いています…」だけを出し、OAuthButtons が押した時と同じ処理を 1 回走らせる。始めなかった・失敗・
  //   プロバイダの画面から「戻る」で帰ってきた（bfcache）→ ふつうの登録の画面（メールでも登録できる）
  const [via] = useState(() => viaFromSearch(location.search));
  const [handoff, setHandoff] = useState(via !== null);
  useEffect(() => {
    const back = (e: PageTransitionEvent) => e.persisted && setHandoff(false);
    window.addEventListener('pageshow', back);
    return () => window.removeEventListener('pageshow', back);
  }, []);
  // 読んだらすぐ URL から invite を消す（Referer・履歴に残さない・WHY は inviteCode.ts の searchWithoutInvite）。state（戻り先など）は持ち越す
  // via も一緒に消す（「戻る」でまた自動で移って抜け出せなくならないように）
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    if (!q.has('invite') && !q.has('via')) return;
    navigate({ pathname: location.pathname, search: searchWithoutVia(searchWithoutInvite(location.search)), hash: location.hash }, { replace: true, state: location.state });
  }, [location, navigate]);
  const signupInvite = useSignupInviteRequirement();
  // 招待の仕組みが無い本体（inviteCredit: null）では欄を出さず、コードも送らない（メールの登録・Google / GitHub の控えの両方）。
  // ただし招待制なら欄は出し、コードも送る（入るための鍵・inviteCode.ts の inviteFieldState / inviteInputToSend）
  const inviteOffer = useInviteCreditOffer();
  const field = inviteFieldState(signupInvite, inviteOffer);
  const inviteInput = inviteInputToSend(inviteOffer, readInviteInput(invite), signupInvite);
  // 登録の欄は空から（覚えているメールを入れない = ログインと登録を行き来しても値を持ち越さない・指示書 9）
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  // 広告から来た登録（サイトの /early のボタン = /signup?invite=…&src=…・2026-10-18）。Google / GitHub の戻りは URL に src が無いので控えの印（back.fromAd）。
  // 広告のコードの枠が埋まった（409 invite_code_used_up / invite_limit_reached）とき、計画 §2 の「This link is full.」を出す（inviteCode.ts の inviteErrorKey）
  // 登録の src（2026-10-19 ops/usage-origin §3）: URL の src をそのまま登録の API に送る（一覧に当たるかはサーバーが決める = apps/server の signupSrc.ts）
  const adSrc = adSourceFromSearch(location.search);
  const fromAd = adSrc !== null || back?.fromAd === true;
  const inviteErrorLine = (code: InviteError) => t(inviteErrorKey(code, fromAd));
  // 広告から来て、招待コードが欄に入っている（形が合う）とき（2026-10-18 gtm/early-fixes・GTM の確認 early-review.md の直す案 5）:
  //   「招待制です・コードが要ります」と欄の説明「入れると…」の代わりに「コードはもう入っています。Google / GitHub ならすぐ」の 1 行。
  //   欄を消した・打ち直して形が変になったら、今までの文に戻る（コードが要ることを伝える）。広告から来ていない人は今までのまま
  const adReady = fromAd && inviteInput.kind === 'ok';
  const [error, setError] = useState<string | null>(() => (back?.inviteError ? inviteErrorLine(back.inviteError) : null));
  const [busy, setBusy] = useState(false);
  const [submits, setSubmits] = useState(0);
  // 招待なしの本体で、付けたコードが使えなかったがアカウントはできた（2026-10-29・components/InviteNotAppliedNotice.tsx）。
  // まだ signIn しない: signIn すると画面の木が替わり（/signup はログイン後の木に無い）、この 1 枚が出る前に消える。押したら signIn して進む
  const [notApplied, setNotApplied] = useState<{ reason: InviteError | 'unknown'; token: string; developer: Developer; to: string } | null>(null);
  const finishSignup = (token: string, developer: Developer, to: string) => {
    // navigate と signIn を同じ transition にまとめる（LoginPage と同じ理由）
    startTransition(() => {
      navigate(to);
      signIn(token, developer);
    });
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmits((n) => n + 1);
    // サーバーの規則（長さ）を先に画面で止めて往復を減らす。正はサーバー
    if (!passwordLengthOk(password)) {
      setError(t('auth.passwordTooShort', { min: MIN_PASSWORD }));
      return;
    }
    // 招待コードの形の違い（打ち間違い）と、招待制で空のときも先に止める。使用済み・上限はサーバーだけが分かる（400 / 409 = アカウントは作られない）
    const inviteError = signupInviteError(signupInvite, inviteInput);
    if (inviteError) {
      setError(t(`error.${inviteError}`));
      return;
    }
    setBusy(true);
    try {
      const body = {
        email,
        password,
        ...(displayName.trim() ? { displayName: displayName.trim() } : {}),
        ...(inviteInput.kind === 'ok' ? { inviteCode: inviteInput.code } : {}),
        // 広告から来た登録（2026-10-18 gtm/early-fixes）: サーバーが確認のメールの「次の 1 歩」を広告の人向けにする（AI の例を Claude Code・Codex から・
        //   最初の頼み「このゲームを公開して」）。古いサーバーは知らない欄を読み捨てる（zod の object は余分なキーを落とす = 断らない）
        ...(fromAd ? { fromAd: true } : {}),
        // 登録の src（上の adSrc）。古いサーバーは読み捨てる
        ...(adSrc ? { src: adSrc } : {})
      };
      const r = await api<{ token: string; developer: Developer; inviteNotApplied?: unknown }>('/v1/developers/signup', { method: 'POST', body, auth: false });
      rem.record(email);
      // 広告から来た登録: この端末に印（ホームの最初の頼み・つなぐ文の締め・adVisit.ts の WHY）
      if (fromAd) markAdVisit(deviceStorage(), Date.now());
      // ログインと同じく元の画面へ（Claude からの連携の途中なら /mcp/authorize に戻る・2026-10-04）
      const to = afterLoginPath((location.state as { from?: unknown } | null)?.from);
      const notAppliedReason = inviteNotAppliedReason(r);
      if (notAppliedReason) {
        // フォームが消える = パスワード管理は「送れた」と読む（下の遷移と同じく保存の案内を出せる）
        setNotApplied({ reason: notAppliedReason, token: r.token, developer: r.developer, to });
        setBusy(false);
        return;
      }
      // 成功したら遷移する（パスワード管理が「新しいアカウントを保存しますか」を出せるように。同じ画面のまま書き換えない）
      finishSignup(r.token, r.developer, to);
    } catch (err) {
      const code = (err as ApiError).code;
      setError(isInviteError(code) ? inviteErrorLine(code) : errorText(err as ApiError));
      setBusy(false);
    }
  };

  if (notApplied) {
    return (
      <AuthLayout>
        <div className="auth-form">
          <InviteNotAppliedNotice
            reason={notApplied.reason}
            fromAd={fromAd}
            onContinue={() => finishSignup(notApplied.token, notApplied.developer, notApplied.to)}
            onBilling={() => finishSignup(notApplied.token, notApplied.developer, INVITE_CODE_PATH)}
          />
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      {handoff && via && (
        <p className="auth-handoff muted" role="status">
          {t('auth.handoff', { provider: PROVIDER_NAMES[via] })}
        </p>
      )}
      {/* handoff の間もフォームは描く（hidden）: OAuthButtons が自動の開始を走らせる・失敗したらそのまま見せる */}
      <form className="auth-form" hidden={handoff} {...authFormProps('signup', '/signup')} onSubmit={(e) => void submit(e)}>
        {/* 広告から来た人の 1 行目（直す案 6）: 「(no free tier)」をやめ、$5 をその最初の分と書く。額は platform/info の値・分からなければ額なし。ほかの人は今まで */}
        <FormTop
          title={t('auth.signupTitle')}
          lead={fromAd ? (inviteOffer.kind === 'offer' ? t('auth.signupLeadAd', { amount: money(inviteOffer.amountCents, inviteOffer.currency) }) : t('auth.signupLeadAdCredit')) : t('auth.signupLead')}
        />
        {/* 登録も同じボタン（プロバイダが確認済みのメールならその場でアカウントができる。同じメールが既にあれば email_exists の案内）。
            登録の画面では「前回使用」の印を出さない（前回の方法 = ログインの話。登録に来た人に既存のアカウントを示唆しない） */}
        {/* 招待コードは外部のボタンより上に置く: Google / GitHub で登録する人も押す前に入れられるように（押した時点で控えに入る） */}
        {/* 招待の仕組みが無い本体（platform/info の inviteCredit が null）では欄ごと出さない = 押しても必ず断られる欄を見せない */}
        {/* 招待制（2026-10-06）では必須: 「(optional)」を出さず required を付け、欄の下に「今は招待制です」の 1 文 */}
        {field.show && (
          <label className="field auth-invite" htmlFor="invite-code">
            <span>
              {t('invite.field')}
              {field.optional && <small>{t('common.optional')}</small>}
            </span>
            {/* name を付けない・autocomplete=off: パスワード管理に覚えさせない（1 回限りのコード） */}
            <input
              id="invite-code"
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              value={invite}
              onChange={(e) => setInvite(e.target.value)}
              maxLength={64}
              required={signupInvite === 'required'}
              aria-describedby={signupInvite === 'required' && !adReady ? 'invite-only invite-hint' : 'invite-hint'}
              aria-invalid={inviteInput.kind === 'invalid' || undefined}
            />
            {signupInvite === 'required' && !adReady && (
              <small id="invite-only" className="muted">
                {t('invite.inviteOnly')}
              </small>
            )}
            <small id="invite-hint" className="muted">
              {/* 額が分かる（新しい本体）なら額を出す。額は platform/info の値 = 画面に直書きしない。キーが無い古い本体は額なしの文 */}
              {/* 広告から来てコードが入っている = adReady の 1 行（上の WHY）。額の有無で 2 つ */}
              {adReady
                ? inviteOffer.kind === 'offer'
                  ? t('invite.adReady', { amount: money(inviteOffer.amountCents, inviteOffer.currency) })
                  : t('invite.adReadyCredit')
                : inviteOffer.kind === 'offer'
                  ? t('invite.fieldHintAmount', { amount: money(inviteOffer.amountCents, inviteOffer.currency) })
                  : t('invite.fieldHintCredit')}
            </small>
          </label>
        )}
        <OAuthButtons returnTo="/" invite={inviteInput} inviteRequirement={signupInvite} fromAd={fromAd} src={adSrc} autoStart={via} onAutoStartEnd={(started) => !started && setHandoff(false)} />
        <div className="auth-fields">
          <label className="field" htmlFor="name">
            <span>
              {t('auth.name')}
              <small>{t('common.optional')}</small>
            </span>
            {/* 表示名。autocomplete=name（アカウント名ではない。username はメールの欄が持つ） */}
            <input id="name" name="name" autoComplete="name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} maxLength={60} />
          </label>
          <label className="field" htmlFor="email">
            <span>{t('auth.email')}</span>
            <input {...emailFieldProps()} value={email} onChange={(e) => setEmail(e.target.value)} required />
          </label>
          <PasswordField
            label={t('auth.password')}
            hideSignal={submits}
            hint={
              <small id="pw-hint" className="muted">
                {t('auth.passwordHint', { min: MIN_PASSWORD })}
              </small>
            }
            inputProps={{ ...passwordFieldProps('new'), value: password, onChange: (e) => setPassword(e.target.value), required: true, 'aria-describedby': 'pw-hint' }}
          />
          <RememberCheck checked={rem.remember} onChange={rem.change} />
          <ErrorLine error={error} />
          <button type="submit" className="btn btn-primary btn-block auth-submit" disabled={busy}>
            {busy ? t('auth.busy') : t('auth.createAccount')}
          </button>
        </div>
        <p className="auth-switch">{tr('auth.haveAccount', { link: <Link to="/login" state={typeof (location.state as { from?: unknown } | null)?.from === 'string' ? { from: (location.state as { from: string }).from } : undefined}>{t('auth.logIn')}</Link> })}</p>
      </form>
    </AuthLayout>
  );
}
